import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const COSMIC_MESSAGES = [
  "宇宙の意志が、あなたの元へ届く道を選んでいます。",
  "星の配置が整うまで、静かにお待ちください。",
  "あなたの周波数と、この品が共鳴する瞬間が近づいています。",
  "宇宙は急がない。しかし、必ず届く。",
];

const DELIVERY_PHRASES = [
  { label: "宇宙の時間軸", value: "7〜14日" },
  { label: "星の配置による調整", value: "±3日" },
  { label: "届くタイミング", value: "宇宙の意志による" },
];

export default function ThanksPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [visible, setVisible] = useState(false);
  const [msgIndex, setMsgIndex] = useState(0);
  const [orbPhase, setOrbPhase] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 200);
    return () => clearTimeout(t);
  }, []);

  // Rotate cosmic messages
  useEffect(() => {
    const interval = setInterval(() => {
      setMsgIndex((i) => (i + 1) % COSMIC_MESSAGES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Orb pulse phase
  useEffect(() => {
    let raf: number;
    const tick = () => {
      setOrbPhase((p) => p + 0.012);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Particle canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const particles: {
      x: number; y: number; r: number;
      dx: number; dy: number; alpha: number; pulse: number; gold: boolean;
    }[] = [];

    for (let i = 0; i < 80; i++) {
      particles.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        r: Math.random() * 1.5 + 0.2,
        dx: (Math.random() - 0.5) * 0.12,
        dy: (Math.random() - 0.5) * 0.12,
        alpha: Math.random() * 0.5 + 0.1,
        pulse: Math.random() * Math.PI * 2,
        gold: Math.random() > 0.5,
      });
    }

    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.pulse += 0.007;
        const a = p.alpha * (0.5 + 0.5 * Math.sin(p.pulse));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.gold
          ? `rgba(201,168,76,${a})`
          : `rgba(255,255,255,${a * 0.4})`;
        ctx.fill();
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });
      animId = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  const orbScale = 1 + 0.04 * Math.sin(orbPhase);
  const orbGlow = 0.5 + 0.5 * Math.sin(orbPhase * 0.7);

  return (
    <div className="min-h-screen bg-[#080608] flex flex-col items-center justify-center relative overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url(https://public.readdy.ai/ai/img_res/fc9e49a855c8a2fe842327ffecb65106.jpg)",
          filter: "brightness(0.25) saturate(0.6)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#080608]/70 via-[#080608]/30 to-[#080608]/90" />

      {/* Particle canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />

      {/* Sacred geometry rings */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          className="rounded-full border border-[#c9a84c]/5"
          style={{ width: 600, height: 600, transform: `scale(${orbScale})` }}
        />
        <div
          className="absolute rounded-full border border-[#c9a84c]/8"
          style={{ width: 450, height: 450, transform: `scale(${1 + 0.03 * Math.sin(orbPhase + 1)})` }}
        />
        <div
          className="absolute rounded-full border border-[#c9a84c]/10"
          style={{ width: 300, height: 300, transform: `scale(${1 + 0.05 * Math.sin(orbPhase + 2)})` }}
        />
      </div>

      {/* Main content */}
      <div
        className="relative z-10 flex flex-col items-center text-center px-6 max-w-2xl w-full"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(30px)",
          transition: "opacity 2s ease, transform 2s ease",
        }}
      >
        {/* Top label */}
        <div
          className="text-[#c9a84c]/50 text-xs tracking-[0.5em] uppercase mb-12"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          REALMS LUXE MINERALS
        </div>

        {/* Central orb / sigil */}
        <div
          className="relative mb-10 flex items-center justify-center"
          style={{
            width: 120,
            height: 120,
            transform: `scale(${orbScale})`,
            transition: "transform 0.1s linear",
          }}
        >
          {/* Glow */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: `radial-gradient(circle, rgba(201,168,76,${0.18 + 0.12 * orbGlow}) 0%, transparent 70%)`,
              filter: "blur(12px)",
            }}
          />
          {/* Ring */}
          <div
            className="absolute inset-0 rounded-full border border-[#c9a84c]/30"
            style={{ boxShadow: `0 0 ${20 + 10 * orbGlow}px rgba(201,168,76,0.15)` }}
          />
          {/* Symbol */}
          <div
            className="text-5xl select-none"
            style={{
              color: `rgba(201,168,76,${0.7 + 0.3 * orbGlow})`,
              textShadow: `0 0 ${30 + 20 * orbGlow}px rgba(201,168,76,0.5)`,
              fontFamily: "'Cormorant Garamond', serif",
            }}
          >
            ✦
          </div>
        </div>

        {/* Main heading */}
        <h1
          className="text-4xl md:text-5xl font-light text-white mb-4 leading-snug"
          style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.04em" }}
        >
          注文が、宇宙に
          <br />
          <span className="text-[#c9a84c]/90">刻まれました。</span>
        </h1>

        <div className="w-12 h-px bg-[#c9a84c]/30 mx-auto my-6" />

        {/* Rotating cosmic message */}
        <div className="h-10 flex items-center justify-center mb-8 overflow-hidden">
          <p
            key={msgIndex}
            className="text-white/40 text-sm tracking-widest"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              animation: "fadeInUp 0.8s ease forwards",
            }}
          >
            {COSMIC_MESSAGES[msgIndex]}
          </p>
        </div>

        {/* Delivery info — cosmic framing */}
        <div
          className="w-full border border-[#c9a84c]/15 rounded-lg p-8 mb-10 relative"
          style={{ background: "rgba(201,168,76,0.03)" }}
        >
          {/* Corner accents */}
          <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-[#c9a84c]/40" />
          <div className="absolute top-0 right-0 w-4 h-4 border-t border-r border-[#c9a84c]/40" />
          <div className="absolute bottom-0 left-0 w-4 h-4 border-b border-l border-[#c9a84c]/40" />
          <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-[#c9a84c]/40" />

          <div
            className="text-[#c9a84c]/60 text-xs tracking-[0.4em] uppercase mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            ― 配送の宇宙的時間軸 ―
          </div>

          <div className="space-y-4">
            {DELIVERY_PHRASES.map((item, i) => (
              <div key={i} className="flex items-center justify-between">
                <span
                  className="text-white/30 text-xs tracking-widest"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {item.label}
                </span>
                <span
                  className="text-[#c9a84c]/70 text-sm"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {item.value}
                </span>
              </div>
            ))}
          </div>

          <div className="w-full h-px bg-[#c9a84c]/10 my-6" />

          <p
            className="text-white/20 text-xs leading-relaxed tracking-wider"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            商品が届くタイミングは、宇宙の意志によって決定されます。
            <br />
            それはあなたが最も必要とする瞬間に、必ず現れます。
          </p>
        </div>

        {/* Order confirmation note */}
        <div className="mb-10 text-center">
          <p className="text-white/25 text-xs tracking-widest leading-loose" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            ご注文の確認メールをお送りしました。
            <br />
            届かない場合は、迷惑メールフォルダをご確認ください。
          </p>
        </div>

        {/* CTA */}
        <Link
          to="/"
          className="inline-block border border-[#c9a84c]/30 text-[#c9a84c]/70 text-xs tracking-[0.4em] uppercase px-10 py-4 hover:border-[#c9a84c]/60 hover:text-[#c9a84c] transition-all duration-500 whitespace-nowrap cursor-pointer"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          宇宙の門へ戻る
        </Link>

        {/* Bottom label */}
        <div className="mt-16 text-[#c9a84c]/20 text-xs tracking-[0.4em] uppercase" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          選ばれし配置 · 内側の周波数と一致する形
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
